import { useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { marketApi } from "@/lib/marketApi";
import type { HeatmapResponse, HeatmapStock, MarketIndex } from "@/types/market";
import { OptionsTab } from "./OptionsTab";
import { ParticipantFlowsCard } from "./ParticipantFlowsCard";
import { StraddleTab } from "./StraddleTab";
import { readStoredTab, storeTab, TabBar, type MarketTab } from "./marketTabs";
import { squarify, type TreemapDatum } from "./treemap";

const POLL_MS = 20_000;
/** |day change| that maps to full color intensity. */
const FULL_INTENSITY_PCT = 2.5;
/** Container aspect the squarify layout targets (w-full at ~2:1). */
const PLANE_ASPECT = 2;

const INDEX_LABELS: Record<MarketIndex, string> = {
  NIFTY50: "NIFTY 50",
  SENSEX30: "SENSEX 30",
};

interface Tile extends TreemapDatum {
  label: string;
  sublabel: string;
  title: string;
  changePct: number;
  drillable: boolean;
  /** Sector tiles only: the sector's heaviest stocks, "SYMBOL w%" lines.
   * `| undefined` is explicit for exactOptionalPropertyTypes. */
  contributors?: string[] | undefined;
}

export function MarketPage(): ReactElement {
  const [index, setIndex] = useState<MarketIndex>("NIFTY50");
  const [sector, setSector] = useState<string | null>(null);
  const [tab, setTab] = useState<MarketTab>(readStoredTab);

  // The heatmap query also feeds the header quote strip, so it polls on
  // every tab — switching to Heatmap is then instant, not a cold load.
  const heatmap = useQuery({
    queryKey: ["market", "heatmap", index],
    queryFn: () => marketApi.heatmap(index),
    refetchInterval: POLL_MS,
  });

  return (
    <main className="mx-auto max-w-6xl px-6 py-6">
      <PageHeader
        data={heatmap.data}
        index={index}
        onIndexChange={(next) => {
          setIndex(next);
          setSector(null);
        }}
      />

      <TabBar
        value={tab}
        onChange={(next) => {
          setTab(next);
          storeTab(next);
        }}
      />

      {tab === "heatmap" && (
        <HeatmapTab heatmap={heatmap} sector={sector} index={index} onSector={setSector} />
      )}
      {tab === "options" && <OptionsTab index={index} />}
      {tab === "straddle" && <StraddleTab />}
      {tab === "participants" && <ParticipantFlowsCard />}
    </main>
  );
}

function HeatmapTab({
  heatmap,
  sector,
  index,
  onSector,
}: Readonly<{
  heatmap: ReturnType<typeof useQuery<HeatmapResponse>>;
  sector: string | null;
  index: MarketIndex;
  onSector: (sector: string | null) => void;
}>): ReactElement {
  const stocks = useMemo(() => heatmap.data?.stocks ?? [], [heatmap.data]);
  const tiles = useMemo(
    () => (sector === null ? sectorTiles(stocks) : stockTiles(stocks, sector)),
    [stocks, sector],
  );
  return (
    <>
      <Breadcrumb
        index={index}
        sector={sector}
        onAllSectors={() => {
          onSector(null);
        }}
      />
      <HeatmapBody
        pending={heatmap.isPending}
        error={heatmap.isError}
        connected={heatmap.data?.connected ?? false}
        tiles={tiles}
        onDrill={onSector}
      />
      <Legend asOf={heatmap.data?.as_of ?? null} />
    </>
  );
}

function PageHeader({
  data,
  index,
  onIndexChange,
}: Readonly<{
  data: HeatmapResponse | undefined;
  index: MarketIndex;
  onIndexChange: (index: MarketIndex) => void;
}>): ReactElement {
  const last = data?.index_last ?? null;
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-4">
        <h1>Market</h1>
        <IndexQuote last={last} changePct={data?.index_change_pct ?? null} />
        {last !== null && <FifteenMinChip move={data?.index_change_15m_points ?? null} />}
      </div>
      <IndexToggle value={index} onChange={onIndexChange} />
    </div>
  );
}

/** Index move over the LAST 15 MINUTES in points — green up, red down.
 * Shows a muted "15m …" while the backend's rolling history warms up
 * (~15 min after the page is first polled). */
function FifteenMinChip({ move }: Readonly<{ move: number | null }>): ReactElement {
  if (move === null) {
    return (
      <span className="pill bg-surface-2 text-ink-muted" title="Collecting 15 minutes of quotes…">
        15m …
      </span>
    );
  }
  const up = move >= 0;
  return (
    <span
      className={cn(
        "pill font-mono",
        up ? "bg-success-bg text-success" : "bg-danger-bg text-danger",
      )}
      title="Index move over the last 15 minutes, in points"
    >
      15m {up ? "▲" : "▼"} {up ? "+" : "-"}
      {Math.abs(move).toLocaleString("en-IN")}
    </span>
  );
}

function HeatmapBody({
  pending,
  error,
  connected,
  tiles,
  onDrill,
}: Readonly<{
  pending: boolean;
  error: boolean;
  connected: boolean;
  tiles: Tile[];
  onDrill: (sector: string) => void;
}>): ReactElement {
  if (pending) {
    return <div className="card text-sm text-ink-secondary">Loading market data…</div>;
  }
  if (error) {
    return <div className="card text-sm text-danger">Could not load the heatmap. Retrying…</div>;
  }
  if (!connected) {
    return (
      <div className="card text-sm text-ink-secondary">
        <i className="ti ti-plug-off mr-1.5" />
        Fyers is not connected — the heatmap needs the live quote feed. Connect Fyers on the{" "}
        <a href="/broker" className="text-accent underline">
          Broker
        </a>{" "}
        page.
      </div>
    );
  }
  if (tiles.length === 0) {
    return <div className="card text-sm text-ink-secondary">No quotes available right now.</div>;
  }
  return (
    <Treemap
      tiles={tiles}
      onTileClick={(id, drillable) => {
        if (drillable) onDrill(id);
      }}
    />
  );
}

function IndexToggle({
  value,
  onChange,
}: Readonly<{ value: MarketIndex; onChange: (index: MarketIndex) => void }>): ReactElement {
  return (
    <div className="flex rounded-ctl border border-line bg-surface-1 p-0.5">
      {(Object.keys(INDEX_LABELS) as MarketIndex[]).map((idx) => (
        <button
          key={idx}
          type="button"
          className={cn(
            "rounded-ctl px-3 py-1.5 text-sm font-medium transition-colors",
            idx === value ? "bg-accent-bg text-accent" : "text-ink-secondary hover:text-ink",
          )}
          onClick={() => {
            onChange(idx);
          }}
        >
          {INDEX_LABELS[idx]}
        </button>
      ))}
    </div>
  );
}

function IndexQuote({
  last,
  changePct,
}: Readonly<{ last: number | null; changePct: number | null }>): ReactElement | null {
  if (last === null) return null;
  const up = (changePct ?? 0) >= 0;
  return (
    <div className="flex items-baseline gap-2">
      <span className="font-mono text-lg">{last.toLocaleString("en-IN")}</span>
      {changePct !== null && (
        <span className={cn("text-sm font-medium", up ? "text-success" : "text-danger")}>
          {up ? "▲" : "▼"} {Math.abs(changePct).toFixed(2)}%
        </span>
      )}
    </div>
  );
}

function Breadcrumb({
  index,
  sector,
  onAllSectors,
}: Readonly<{
  index: MarketIndex;
  sector: string | null;
  onAllSectors: () => void;
}>): ReactElement {
  return (
    <div className="mb-3 flex items-center gap-1.5 text-sm">
      {sector === null ? (
        <span className="text-ink-secondary">
          {INDEX_LABELS[index]} · all sectors — click a sector to drill in
        </span>
      ) : (
        <>
          <button type="button" className="text-accent hover:underline" onClick={onAllSectors}>
            <i className="ti ti-arrow-left mr-1" />
            All sectors
          </button>
          <span className="text-ink-muted">/</span>
          <span className="font-medium">{sector}</span>
        </>
      )}
    </div>
  );
}

function Treemap({
  tiles,
  onTileClick,
}: Readonly<{
  tiles: Tile[];
  onTileClick: (id: string, drillable: boolean) => void;
}>): ReactElement {
  const laid = useMemo(() => squarify(tiles, PLANE_ASPECT), [tiles]);
  return (
    <div
      className="relative w-full overflow-hidden rounded-xl border border-line"
      style={{ aspectRatio: `${String(PLANE_ASPECT)} / 1` }}
    >
      {laid.map(({ datum, x, y, w, h }) => {
        const big = w > 9 && h > 14;
        return (
          <button
            key={datum.id}
            type="button"
            title={datum.title}
            className={cn(
              "absolute overflow-hidden border border-surface-0 p-1 text-left transition-opacity",
              datum.drillable ? "cursor-pointer hover:opacity-85" : "cursor-default",
            )}
            style={tileStyle(datum.changePct, x, y, w, h)}
            onClick={() => {
              onTileClick(datum.id, datum.drillable);
            }}
          >
            <div className={cn("truncate font-medium", big ? "text-sm" : "text-[10px]")}>
              {datum.label}
            </div>
            <div className={cn("truncate", big ? "text-xs" : "text-[9px]")}>{datum.sublabel}</div>
            {big && datum.contributors !== undefined && (
              <div className="mt-0.5">
                {datum.contributors.map((line) => (
                  <div key={line} className="truncate text-[10px] opacity-90">
                    {line}
                  </div>
                ))}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Diverging fill for a signed intensity in [-1, 1]: success/danger mixed
 * into the surface (12%..85% of the pole color), neutral gray near zero. */
function divergingFill(intensity: number): string {
  if (Math.abs(intensity) < 0.02) {
    return "color-mix(in oklab, var(--text-muted) 18%, var(--surface-2))";
  }
  const pole = intensity > 0 ? "var(--fill-success)" : "var(--fill-danger)";
  const mix = Math.round(12 + Math.abs(intensity) * 73);
  return `color-mix(in oklab, ${pole} ${String(mix)}%, var(--surface-2))`;
}

/** Text flips to white once the fill goes deep (past ~mid intensity). */
function tileStyle(changePct: number, x: number, y: number, w: number, h: number): CSSProperties {
  const intensity = Math.max(-1, Math.min(changePct / FULL_INTENSITY_PCT, 1));
  return {
    left: `${String(x)}%`,
    top: `${String(y)}%`,
    width: `${String(w)}%`,
    height: `${String(h)}%`,
    background: divergingFill(intensity),
    color: Math.abs(intensity) > 0.49 ? "#ffffff" : "var(--text-primary)",
  };
}

function sectorTiles(stocks: readonly HeatmapStock[]): Tile[] {
  const bySector = new Map<string, HeatmapStock[]>();
  for (const s of stocks) {
    const group = bySector.get(s.sector) ?? [];
    group.push(s);
    bySector.set(s.sector, group);
  }
  return [...bySector.entries()].map(([sector, group]) => {
    const weight = group.reduce((a, s) => a + s.weight, 0);
    const change = group.reduce((a, s) => a + s.change_pct * s.weight, 0) / (weight || 1);
    const contributors = topContributors(group);
    return {
      id: sector,
      value: weight,
      label: sector,
      sublabel: `${formatPct(change)} · ${String(group.length)} ${group.length === 1 ? "stock" : "stocks"}`,
      title: `${sector} — ${weight.toFixed(1)}% of index, ${formatPct(change)} · top: ${contributors.join(", ")}`,
      changePct: change,
      drillable: true,
      contributors,
    };
  });
}

/** The sector's heaviest index constituents (top 3 by weight), each line
 * showing the stock's day change — "SYMBOL +0.73%". */
function topContributors(group: readonly HeatmapStock[]): string[] {
  return [...group]
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 3)
    .map((s) => `${s.symbol} ${formatPct(s.change_pct)}`);
}

function stockTiles(stocks: readonly HeatmapStock[], sector: string): Tile[] {
  return stocks
    .filter((s) => s.sector === sector)
    .map((s) => ({
      id: s.symbol,
      value: s.weight,
      label: s.symbol,
      sublabel: formatPct(s.change_pct),
      title: `${s.name} — ₹${s.last_price.toLocaleString("en-IN")} (${formatPct(s.change_pct)}), ${s.weight.toFixed(2)}% of index`,
      changePct: s.change_pct,
      drillable: false,
    }));
}

function formatPct(pct: number): string {
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

function Legend({ asOf }: Readonly<{ asOf: string | null }>): ReactElement {
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-secondary">
      <div className="flex items-center gap-2">
        <span>-{FULL_INTENSITY_PCT}%</span>
        <div className="flex h-3 overflow-hidden rounded-sm">
          {[-1, -0.6, -0.25, 0, 0.25, 0.6, 1].map((step) => (
            <div key={step} className="w-6" style={{ background: divergingFill(step) }} />
          ))}
        </div>
        <span>+{FULL_INTENSITY_PCT}%</span>
        <span className="ml-2">tile size = index weight</span>
      </div>
      {asOf !== null && (
        <span>as of {new Date(asOf).toLocaleTimeString("en-IN", { hour12: false })}</span>
      )}
    </div>
  );
}
